import { useQuery } from '@tanstack/react-query'
import { api, type CoachAwayFixture } from '../lib/api'
import { CardSection } from './ui/CardSection'

/** date · opponent · location · "time not yet published" when known_time is
 *  still null (design spec §7). Exported for its tests.
 *
 *  Both fallbacks are words, not punctuation: no em dashes in prose a coach
 *  reads (CLAUDE.md, .impeccable.md). They say different things on purpose —
 *  an away kickoff genuinely arrives later, so "not yet published" is a
 *  promise the Extra program keeps, while a location is known before Generate,
 *  so a blank one is a gap in our own record rather than something pending.
 *  Unreachable in practice anyway: `normalizeFixture` requires a location. */
export function awayFixtureLine(f: CoachAwayFixture): string {
  const where = f.location_text ?? 'location unknown'
  return `${f.date} · ${f.opponent_label} · ${where} · ${f.known_time ?? 'time not yet published'}`
}

/** A small block of this team's away fixtures (Home and Games) — games the Extra program plays
 *  entirely off-platform, so they never appear in the ordinary schedule list
 *  (only a home fixture becomes a `games` row). Renders nothing when the team
 *  has none, same as every other "nothing to show" panel on Home and Games. */
export function AwayGamesCard({ teamExternalId }: { teamExternalId: string }) {
  const q = useQuery({
    queryKey: ['away-fixtures', teamExternalId],
    queryFn: () => api.awayFixtures(teamExternalId),
  })
  const fixtures = q.data?.fixtures ?? []
  if (q.isLoading || q.isError || fixtures.length === 0) return null

  return (
    <CardSection title="Away games">
      <ul className="flex flex-col gap-1 text-sm text-ink">
        {fixtures.map((f, i) => (
          <li key={`${f.date}-${i}`}>{awayFixtureLine(f)}</li>
        ))}
      </ul>
    </CardSection>
  )
}
